<template>
  <b-modal
    id="modal-business"
    :title="modalTitle"
    size="lg"
    :hide-footer="viewDetail"
    @hidden="handleClose"
    scrollable
  >
    <div v-if="viewDetail">
      <!-- View Mode -->
      <div class="row">
        <div class="col-md-6">
          <div class="form-group">
            <label class="font-weight-bold">Business Name:</label>
            <p>{{ business.businessName || business.name || 'N/A' }}</p>
          </div>
        </div>
        <div class="col-md-6">
          <div class="form-group">
            <label class="font-weight-bold">NTN Number:</label>
            <p>{{ business.ntn || business.ntnNumber || 'N/A' }}</p>
          </div>
        </div>
      </div>
      <div class="row">
        <div class="col-md-6">
          <div class="form-group">
            <label class="font-weight-bold">STRN Number:</label>
            <p>{{ business.strn || business.gstNumber || business.gstNo || 'N/A' }}</p>
          </div>
        </div>
        <div class="col-md-6">
          <div class="form-group">
            <label class="font-weight-bold">CNIC:</label>
            <p>{{ business.cnic || 'N/A' }}</p>
          </div>
        </div>
      </div>
      <div class="row">
        <div class="col-md-6">
          <div class="form-group">
            <label class="font-weight-bold">Phone:</label>
            <p>{{ business.phone || 'N/A' }}</p>
          </div>
        </div>
        <div class="col-md-6">
          <div class="form-group">
            <label class="font-weight-bold">Email:</label>
            <p>{{ business.email || 'N/A' }}</p>
          </div>
        </div>
      </div>
      <div class="row">
        <div class="col-md-6">
          <div class="form-group">
            <label class="font-weight-bold">Address:</label>
            <p>{{ business.address || 'N/A' }}</p>
          </div>
        </div>
        <div class="col-md-6">
          <div class="form-group">
            <label class="font-weight-bold">Province:</label>
            <p>{{ business.province || 'N/A' }}</p>
          </div>
        </div>
      </div>
      <div class="row">
        <div class="col-md-6">
          <div class="form-group">
            <label class="font-weight-bold">Website:</label>
            <p>{{ business.website || 'N/A' }}</p>
          </div>
        </div>
      </div>
      <div class="row">
        <div class="col-md-6">
          <div class="form-group">
            <label class="font-weight-bold">Created On:</label>
            <p>{{ formatDate(business.createdDate) || 'N/A' }}</p>
          </div>
        </div>
        <div class="col-md-6">
          <div class="form-group">
            <label class="font-weight-bold">Updated On:</label>
            <p>{{ formatDate(business.updatedDate) || 'N/A' }}</p>
          </div>
        </div>
      </div>
    </div>

    <div v-else>
      <!-- Add/Edit Mode -->
      <div class="row">
        <div class="col-md-6">
          <b-form-group :label="`Business Name ${!isEditing ? '*' : ''}`" label-for="businessName">
            <b-form-input
              id="businessName"
              v-model="businessForm.businessName"
              :state="validateBusinessName"
              placeholder="Enter business name"
              required
            />
            <b-form-invalid-feedback :state="validateBusinessName">
              Business name is required
            </b-form-invalid-feedback>
          </b-form-group>
        </div>
        <div class="col-md-6">
          <b-form-group :label="`NTN Number ${!isEditing ? '*' : ''}`" label-for="ntnNumber">
            <b-form-input
              id="ntnNumber"
              v-model="businessForm.ntnNumber"
              :state="validateNtn"
              placeholder="Enter NTN number (F123456-7 or 1234567)"
              v-mask="ntnMask"
              @input="handleNtnInput"
              @blur="handleNtnBlur"
            />
            <b-form-invalid-feedback :state="validateNtn">
              NTN is required when adding a new business
            </b-form-invalid-feedback>
          </b-form-group>
        </div>
      </div>

      <div class="row">
        <div class="col-md-6">
          <b-form-group :label="`Address ${!isEditing ? '*' : ''}`" label-for="address">
            <b-form-textarea
              id="address"
              v-model="businessForm.address"
              :state="validateAddress"
              placeholder="Enter business address"
              rows="3"
            />
            <b-form-invalid-feedback :state="validateAddress">
              Address is required when adding a new business
            </b-form-invalid-feedback>
          </b-form-group>
        </div>
        <div class="col-md-6">
          <b-form-group label="Phone" label-for="phone">
            <b-form-input
              id="phone"
              v-model="businessForm.phone"
              placeholder="Enter phone number (+92##########)"
              v-mask="'+92##########'"
            />
          </b-form-group>
        </div>
      </div>
      <div class="row">
        <div class="col-md-6">
          <b-form-group label="Email" label-for="email">
            <b-form-input
              id="email"
              v-model="businessForm.email"
              type="email"
              placeholder="Enter email address"
            />
          </b-form-group>
        </div>
        <div class="col-md-6">
          <b-form-group label="Website" label-for="website">
            <b-form-input
              id="website"
              v-model="businessForm.website"
              placeholder="Enter website URL"
            />
          </b-form-group>
        </div>
      </div>
      <div class="row">
        <div class="col-md-6">
          <b-form-group label="Province" label-for="province">
            <v-select
              id="province"
              v-model="businessForm.province"
              :options="provinceOptions"
              placeholder="Select province"
              label="text"
              :reduce="option => option.value"
              :clearable="false"
            />
          </b-form-group>
        </div>
        <div class="col-md-6">
          <b-form-group :label="`CNIC ${!isEditing ? (hasNtn ? '' : '*') : ''}`" label-for="cnic">
            <b-form-input
              id="cnic"
              v-model="businessForm.cnic"
              :state="validateCnic"
              placeholder="Enter CNIC number (13 digits)"
              v-mask="'#####-#######-#'"
            />
            <b-form-invalid-feedback :state="validateCnic">
              {{ hasNtn ? 'CNIC is optional when NTN is provided' : 'CNIC is required when NTN is not provided' }}
            </b-form-invalid-feedback>
          </b-form-group>
        </div>
      </div>

      <div class="row">
        <div class="col-md-6">
          <b-form-group label="STRN Number" label-for="strn">
            <b-form-input
              id="strn"
              v-model="businessForm.strn"
              placeholder="Enter STRN number"
              v-mask="'##-##-####-###-##'"
            />
          </b-form-group>
        </div>
      </div>

      <!-- Username and Password fields for new business creation -->
      <div v-if="!isEditing" class="row">
        <div class="col-md-6">
          <b-form-group label="Username *" label-for="username">
            <b-form-input
              id="username"
              v-model="businessForm.username"
              :state="validateUsername"
              placeholder="Enter username"
            />
            <b-form-invalid-feedback :state="validateUsername">
              Username is required
            </b-form-invalid-feedback>
          </b-form-group>
        </div>
        <div class="col-md-6">
          <b-form-group label="Password *" label-for="password">
            <b-form-input
              id="password"
              v-model="businessForm.password"
              type="password"
              :state="validatePassword"
              placeholder="Enter password"
            />
            <b-form-invalid-feedback :state="validatePassword">
              Password is required
            </b-form-invalid-feedback>
          </b-form-group>
        </div>
      </div>
      
      <!-- Confirm Password field for new business creation -->
      <div v-if="!isEditing" class="row">
        <div class="col-md-6">
          <b-form-group label="Confirm Password *" label-for="confirmPassword">
            <b-form-input
              id="confirmPassword"
              v-model="businessForm.confirmPassword"
              type="password"
              :state="validateConfirmPassword"
              placeholder="Confirm password"
            />
            <b-form-invalid-feedback :state="validateConfirmPassword">
              Passwords do not match
            </b-form-invalid-feedback>
          </b-form-group>
        </div>
      </div>

      <div class="row">
        <div class="col-md-6">
          <b-form-group :label="`Integration Mode ${!isEditing ? '*' : ''}`" label-for="integrationMode">
            <v-select
              id="integrationMode"
              v-model="businessForm.integrationMode"
              :options="integrationModeOptions"
              placeholder="Select integration mode"
              label="text"
              :reduce="option => option.value"
              :clearable="false"
              :state="validateIntegrationMode"
            />
            <b-form-invalid-feedback :state="validateIntegrationMode">
              Integration mode is required
            </b-form-invalid-feedback>
          </b-form-group>
        </div>
        <div class="col-md-6">
          <b-form-group :label="`Staging Token ${!isEditing && businessForm.integrationMode === 'Staging' ? '*' : ''}`" label-for="stagingToken">
            <b-form-input
              id="stagingToken"
              v-model="businessForm.stagingToken"
              :state="validateStagingToken"
              placeholder="Enter staging token"
            />
            <b-form-invalid-feedback :state="validateStagingToken">
              Staging token is required when Integration Mode is Staging
            </b-form-invalid-feedback>
          </b-form-group>
        </div>
     
      </div>
      <div class="row">
        <div class="col-md-6">
          <b-form-group :label="`Live Token ${!isEditing && businessForm.integrationMode === 'Live' ? '*' : ''}`" label-for="liveToken">
            <b-form-input
              id="liveToken"
              v-model="businessForm.liveToken"
              :state="validateLiveToken"
              placeholder="Enter live token"
            />
            <b-form-invalid-feedback :state="validateLiveToken">
              Live token is required when Integration Mode is Live
            </b-form-invalid-feedback>
          </b-form-group>
        </div>
      </div>
    </div>

    <!-- Footer Buttons -->
    <template #modal-footer v-if="!viewDetail">
      <b-button type="button" variant="secondary" @click="handleClose"> Cancel </b-button>
      <b-button variant="primary" @click="handleSave" :disabled="!isFormValid || isSaving">
        <b-spinner small v-if="isSaving"></b-spinner>
        {{ isEditing ? 'Update' : 'Save' }}
      </b-button>
    </template>
  </b-modal>
</template>

<script>
import { APIService } from '@/utils/api';
import { RESOURCES } from '@/utils/constants';
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue';
import {
  BButton,
  BFormGroup,
  BFormInput,
  BFormSelect,
  BFormTextarea,
  BFormInvalidFeedback,
  BSpinner,
  BBadge,
} from 'bootstrap-vue';
import vSelect from 'vue-select';

export default {
  name: 'BusinessModal',
  components: {
    BButton,
    BFormGroup,
    BFormInput,
    BFormSelect,
    BFormTextarea,
    BFormInvalidFeedback,
    BSpinner,
    BBadge,
    vSelect,
  },
  props: {
    isEditing: {
      type: Boolean,
      default: false,
    },
    viewDetail: {
      type: Boolean,
      default: false,
    },
    business: {
      type: Object,
      default: () => ({}),
    },
    businessID: {
      type: [Number, String],
      required: true,
    },
    userID: {
      type: [Number, String],
      required: true,
    },
  },
  data() {
    return {
      RESOURCES,
      isSaving: false,
      businessForm: {
        businessName: '',
        ntnNumber: '',
        address: '',
        phone: '',
        email: '',
        website: '',
        province: '',
        cnic: '',
        strn: '',
        username: '',
        password: '',
        confirmPassword: '',
        stagingToken: '',
        liveToken: '',
        integrationMode: '',
      },
      provinceOptions: [
        { value: 'Punjab', text: 'Punjab' },
        { value: 'Sindh', text: 'Sindh' },
        { value: 'Khyber Pakhtunkhwa', text: 'Khyber Pakhtunkhwa' },
        { value: 'Balochistan', text: 'Balochistan' },
        { value: 'Gilgit-Baltistan', text: 'Gilgit-Baltistan' },
      ],
      integrationModeOptions: [
        { value: 'Live', text: 'Live' },
        { value: 'Staging', text: 'Staging' },
      ],
    };
  },
  computed: {
    modalTitle() {
      if (this.viewDetail) return 'View Business Details';
      return this.isEditing ? 'Edit Business' : 'Add New Business';
    },
    validateBusinessName() {
      return this.businessForm.businessName.length > 0;
    },
    isFormValid() {
      if (this.isEditing) {
        return this.validateBusinessName;
      }
      
      // For new business creation, ensure at least one of CNIC or NTN is properly filled
      const hasValidNtn = this.hasNtn;
      const hasCnic = (this.businessForm.cnic || '').toString().trim().length > 0;
      const hasEitherCnicOrNtn = hasValidNtn || hasCnic;
      
      return (
        this.validateBusinessName &&
        this.validateAddress &&
        hasEitherCnicOrNtn &&
        this.validateUsername &&
        this.validatePassword &&
        this.validateConfirmPassword &&
        this.validateIntegrationMode &&
        this.validateStagingToken &&
        this.validateLiveToken
      );
    },
    validateNtn() {
      if (this.isEditing) return true;
      
      const ntnValue = (this.businessForm.ntnNumber || '').toString().trim();
      const cnicValue = (this.businessForm.cnic || '').toString().trim();
      
      // Check if CNIC is filled with proper format (13 digits with dashes)
      const hasProperCnic = cnicValue.length > 0 && /^\d{5}-\d{7}-\d{1}$/.test(cnicValue);
      
      // If CNIC is properly filled, NTN is optional
      if (hasProperCnic) return true;
      
      // If CNIC is not properly filled, NTN must be properly formatted
      if (ntnValue.length === 0) return false;
      
      // If starts with letter, should be in format A123456-7 (9 characters total)
      if (/^[A-Za-z]/.test(ntnValue)) {
        return /^[A-Za-z]\d{6,7}(-\d{1})?$/.test(ntnValue);
      }
      
      // If starts with digit, should be exactly 7 digits
      if (/^\d/.test(ntnValue)) {
        return /^\d{7}$/.test(ntnValue);
      }
      
      return false;
    },
    validateAddress() {
      if (this.isEditing) return true;
      return (this.businessForm.address || '').toString().trim().length > 0;
    },
    validateCnic() {
      if (this.isEditing) return true;
      
      const hasValidNtn = this.hasNtn; // Use the strict validation for NTN
      const hasCnic = (this.businessForm.cnic || '').toString().trim().length > 0;
      
      // If NTN is properly filled, CNIC is optional (show as valid)
      if (hasValidNtn) return true;
      
      // If NTN is not properly filled, CNIC must be filled
      return hasCnic;
    },
    validateUsername() {
      if (this.isEditing) return true;
      return (this.businessForm.username || '').toString().trim().length > 0;
    },
    validatePassword() {
      if (this.isEditing) return true;
      return (this.businessForm.password || '').toString().trim().length > 0;
    },
    validateConfirmPassword() {
      if (this.isEditing) return true;
      if (!this.businessForm.password || !this.businessForm.confirmPassword) return false;
      return this.businessForm.password === this.businessForm.confirmPassword;
    },
    hasNtn() {
      if (this.isEditing) return true;
      const ntnValue = (this.businessForm.ntnNumber || '').toString().trim();
      
      // NTN should be at least 7 digits or properly formatted with letter
      if (ntnValue.length === 0) return false;
      
      // If starts with letter, should be in format A123456-7 (9 characters total)
      if (/^[A-Za-z]/.test(ntnValue)) {
        return /^[A-Za-z]\d{6,7}(-\d{1})?$/.test(ntnValue);
      }
      
      // If starts with digit, should be exactly 7 digits
      if (/^\d/.test(ntnValue)) {
        return /^\d{7}$/.test(ntnValue);
      }
      
      return false;
    },
    validateIntegrationMode() {
      if (this.isEditing) return true;
      return (this.businessForm.integrationMode || '').toString().trim().length > 0;
    },
    validateStagingToken() {
      if (this.isEditing) return true;
      // Required only when Integration Mode is 'Staging'
      if ((this.businessForm.integrationMode || '').toString().trim() === 'Staging') {
        return (this.businessForm.stagingToken || '').toString().trim().length > 0;
      }
      return true;
    },
    validateLiveToken() {
      if (this.isEditing) return true;
      // Required only when Integration Mode is 'Live'
      if ((this.businessForm.integrationMode || '').toString().trim() === 'Live') {
        return (this.businessForm.liveToken || '').toString().trim().length > 0;
      }
      return true;
    },
    // Dynamic NTN mask based on input
    ntnMask() {
      const ntnValue = this.businessForm.ntnNumber || '';
      // If starts with alphabet, use full format (A123456-7)
      if (ntnValue.length > 0 && /^[A-Za-z]/.test(ntnValue)) {
        return 'A######-#';
      }
      // If starts with digit or empty, use 7 digits only
      return '#######';
    },
  },
  watch: {
    business: {
      handler(newBusiness) {
        if (newBusiness && Object.keys(newBusiness).length > 0) {
          this.populateForm(newBusiness);
        } else {
          this.resetForm();
        }
      },
      immediate: true,
      deep: true,
    },
  },
  methods: {
    populateForm(businessData) {
      this.businessForm = {
        businessName: businessData.businessName || businessData.name || '',
        ntnNumber: businessData.ntn || businessData.ntnNumber || '',
        address: businessData.address || '',
        phone: businessData.phone || '',
        email: businessData.email || '',
        website: businessData.website || '',
        province: businessData.province || '',
        cnic: businessData.cnic || '',
        strn: businessData.strn || businessData.gstNumber || businessData.gstNo || '',
        username: businessData.username || '',
        password: businessData.password || '',
        confirmPassword: businessData.confirmPassword || '',
        stagingToken: businessData.stagingToken || '',
        liveToken: businessData.liveToken || '',
        integrationMode: businessData.integrationMode || '',
      };
    },
    resetForm() {
      this.businessForm = {
        businessName: '',
        ntnNumber: '',
        address: '',
        phone: '',
        email: '',
        website: '',
        province: '',
        cnic: '',
        strn: '',
        username: '',
        password: '',
        confirmPassword: '',
        stagingToken: '',
        liveToken: '',
        integrationMode: '',
      };
    },
    formatDate(dateString) {
      if (!dateString) return 'N/A';
      try {
        return new Date(dateString).toLocaleDateString();
      } catch (error) {
        return dateString;
      }
    },

    // Handle NTN input with dynamic formatting
    handleNtnInput(value) {
      // Clean the input value
      let cleanValue = value.replace(/[^A-Za-z0-9-]/g, '');

      if (cleanValue.length > 0) {
        // If starts with alphabet
        if (/^[A-Za-z]/.test(cleanValue)) {
          // Format: F123456-7 (letter + 6 digits + dash + 1 digit)
          const letterPart = cleanValue.substring(0, 1).toUpperCase();
          const numberPart = cleanValue.substring(1).replace(/[^0-9-]/g, '');

          if (numberPart.includes('-')) {
            // Handle existing dash, ensure proper format
            const parts = numberPart.split('-');
            if (parts.length === 2 && parts[0].length <= 6 && parts[1].length <= 1) {
              cleanValue = letterPart + parts[0] + '-' + parts[1];
            } else {
              // Remove dash and reformat
              const digitsOnly = numberPart.replace('-', '');
              if (digitsOnly.length >= 7) {
                cleanValue =
                  letterPart + digitsOnly.substring(0, 6) + '-' + digitsOnly.substring(6, 7);
              } else {
                cleanValue = letterPart + digitsOnly;
              }
            }
          } else {
            // No dash present
            if (numberPart.length >= 7) {
              // Auto-add dash: A123456-7
              cleanValue =
                letterPart + numberPart.substring(0, 6) + '-' + numberPart.substring(6, 7);
            } else if (numberPart.length <= 6) {
              // Still typing, don't add dash yet
              cleanValue = letterPart + numberPart;
            } else {
              // Limit to 7 digits max
              cleanValue = letterPart + numberPart.substring(0, 7);
            }
          }

          // Ensure we don't exceed the maximum length (A123456-7 = 9 chars)
          if (cleanValue.length > 9) {
            cleanValue = cleanValue.substring(0, 9);
          }
        } else {
          // If starts with digit, limit to 7 digits only
          cleanValue = cleanValue.replace(/[^0-9]/g, '').substring(0, 7);
        }
      }

      this.businessForm.ntnNumber = cleanValue;
    },

    // Handle NTN blur to ensure proper formatting
    handleNtnBlur() {
      this.businessForm.ntnNumber = this.formatNtnForSubmission(this.businessForm.ntnNumber);
    },

    // Format NTN before submission
    formatNtnForSubmission(ntn) {
      if (!ntn || ntn.trim().length === 0) return ntn;

      const cleanNtn = ntn.trim();

      // If starts with letter and has 7+ digits after letter
      if (/^[A-Za-z]/.test(cleanNtn)) {
        const letter = cleanNtn.substring(0, 1).toUpperCase();
        const digits = cleanNtn.substring(1).replace(/[^0-9]/g, '');

        // If we have exactly 7 digits, format as A123456-7
        if (digits.length === 7) {
          return letter + digits.substring(0, 6) + '-' + digits.substring(6);
        }
        // If we have 6 digits, it's incomplete - return as is
        else if (digits.length === 6) {
          return letter + digits;
        }
        // If already properly formatted, return as is
        else if (/^[A-Za-z]\d{6}-\d{1}$/.test(cleanNtn)) {
          return cleanNtn.toUpperCase();
        }
      }

      return cleanNtn;
    },
    async handleSave() {
      if (!this.isFormValid) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Missing required info',
            icon: 'AlertTriangleIcon',
            variant: 'warning',
            text: 'Please fill all required fields before saving.',
          },
        });
        return;
      }

      this.isSaving = true;

      try {
        const payload = {
          ...this.businessForm,
          businessID: this.businessID,
        };

        // Remove fields that should be ignored according to SOP
        const fieldsToIgnore = [
          'addedBy',
          'addedOn',
          'updatedBy',
          'updatedOn',
          'metaAddedBy',
          'metaUpdatedBy',
          'isDeleted',
          'confirmPassword', // Remove confirm password from payload
        ];
        fieldsToIgnore.forEach((field) => delete payload[field]);

        let response;
        if (this.isEditing && this.business.id) {
          // Update existing business using the Save API endpoint
          const updatePayload = {
            id: this.business.id,
            businessID: this.businessID,
            name: payload.businessName,
            logo: payload.logo || '',
            address: payload.address,
            phone: payload.phone,
            email: payload.email,
            website: payload.website,
            stagingToken: payload.stagingToken,
            liveToken: payload.liveToken,
            integrationMode: payload.integrationMode,
            businessName: payload.businessName,
            ntn: this.formatNtnForSubmission(payload.ntnNumber),
            cnic: payload.cnic,
            strn: payload.strn || '',
            province: payload.province,
          };

          response = await APIService.post('Business/Save', updatePayload, {
            params: { userID: this.userID },
          });
        } else {
          // Create new business using the specific API endpoint
          const createPayload = {
            business: {
              id: 0,
              businessID: this.businessID,
              name: payload.businessName,
              logo: '',
              address: payload.address,
              phone: payload.phone,
              email: payload.email,
              website: payload.website,
              stagingToken: payload.stagingToken,
              liveToken: payload.liveToken,
              integrationMode: payload.integrationMode,
              createdDate: new Date().toISOString(),
              updatedDate: new Date().toISOString(),
              isDeleted: false,
              businessName: payload.businessName,
              ntn: this.formatNtnForSubmission(payload.ntnNumber),
              cnic: payload.cnic,
              strn: payload.strn || '',
              province: payload.province,
            },
            username: payload.username,
            password: payload.password,
          };

          response = await APIService.post('Business/InitNewBusiness', createPayload, {
            params: { userID: 58 },
          });
        }

        if (response?.data?.status === 'success' || response?.data) {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Success',
              icon: 'CheckIcon',
              variant: 'success',
              text: this.isEditing
                ? 'Business updated successfully'
                : 'Business created successfully',
            },
          });

          this.$emit('saved');
          this.$bvModal.hide('modal-business');
        } else {
          throw new Error(response?.data?.message || 'Failed to save business');
        }
      } catch (error) {
        console.error('Error saving business:', error);
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Error',
            icon: 'AlertCircleIcon',
            variant: 'danger',
            text: error.response?.data?.message || error.message || 'Failed to save business',
          },
        });
      } finally {
        this.isSaving = false;
      }
    },
    handleClose() {
      this.$emit('close');
      this.resetForm();
    },
  },
};
</script>

<style lang="scss" scoped>
.form-group {
  margin-bottom: 1rem;
}

.font-weight-bold {
  font-weight: 600;
  color: #6e6b7b;
  margin-bottom: 0.5rem;
}

p {
  margin-bottom: 0;
  padding: 0.5rem;
  background-color: #f8f8f8;
  border-radius: 0.358rem;
  border: 1px solid #ddd;
}
</style>
